import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DetailsCard, ReactTable } from "components";
import "modules/CarDetails/components/AppointmentHistory/AppointmentHistory.scss";
import { AppointmentHistoryHeader } from "modules/CarDetails/components/AppointmentHistory/components";

import type { SectionMenu } from "components";
import type { Appointment, Checklist } from "models";

type AppointmentHistoryProps = {
  appointments?: Appointment[];
  sectionMenu: SectionMenu;
  isExpanded: boolean;
  holdingHistory?: boolean;
};

const checkListColumnHelper = createColumnHelper<Checklist>();

export const AppointmentHistory = ({ appointments, sectionMenu, isExpanded, holdingHistory = false }: AppointmentHistoryProps) => {
  const { t } = useTranslation();

  const checkListColumns = useMemo(
    () => [
      checkListColumnHelper.accessor(row => row, {
        id: "name",
        cell: info => (
          <Popup hoverable position="top center" content={info.row.original.name} trigger={<p className="ellipsis medium name-cell">{info.row.original.name}</p>} />
        )
      }),
      ...(holdingHistory
        ? [
            checkListColumnHelper.accessor(row => row, {
              id: "dealer_name",
              cell: () => null
            })
          ]
        : []),
      checkListColumnHelper.accessor(row => row, {
        id: "images_count",
        cell: info => info.row.original.images_count
      }),
      checkListColumnHelper.accessor(row => row, {
        id: "necessary_count",
        cell: info => info.row.original.necessary_count
      }),
      checkListColumnHelper.accessor(row => row, {
        id: "necessary_sold_count",
        cell: info => info.row.original.necessary_sold_count
      }),
      checkListColumnHelper.accessor(row => row, {
        id: "advised_count",
        cell: info => info.row.original.advised_count
      }),
      checkListColumnHelper.accessor(row => row, {
        id: "advised_sold_count",
        cell: info => info.row.original.advised_sold_count
      }),
      checkListColumnHelper.accessor(row => row, {
        id: "icons",
        cell: () => <div />
      })
    ],
    [holdingHistory]
  );

  if (!sectionMenu.count || !appointments) return null;

  return (
    <DetailsCard isExpanded={isExpanded} sectionMenuId={sectionMenu.id} icon={sectionMenu.icon} title={`${sectionMenu.title} (${sectionMenu.count})`}>
      <div className={`AppointmentHistory ${holdingHistory ? "holding" : ""}`}>
        {appointments.map(appointment => (
          <ReactTable
            key={`appointment-checklist-${appointment.id}`}
            data={appointment.checklists}
            columns={checkListColumns}
            emptyRowMessage={t("v8_no_checklists_in_the_appointment", "No checklists in the appointment")}
            customHeader={() => (
              <>
                <tr>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"time_car_app"} />
                  </th>
                  {holdingHistory && (
                    <th>
                      <AppointmentHistoryHeader appointment={appointment} propertyName={"dealer_name"} />
                    </th>
                  )}
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"location_name"} />
                  </th>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"next_date"} />
                  </th>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"current_km"} />
                  </th>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"next_km"} />
                  </th>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"reg_number"} />
                  </th>
                  <th>
                    <AppointmentHistoryHeader appointment={appointment} propertyName={"id"} holdingHistory={holdingHistory} />
                  </th>
                </tr>
                <tr />
              </>
            )}
          />
        ))}
      </div>
    </DetailsCard>
  );
};
